import { Injectable } from '@angular/core'
import { mapProductionTechnology, ProductionTechnology } from '../models/production-technology.enum'
import { SolarTechnicalResource, WindTechnicalResource } from '../models/technical-resource'
import {
  OTHER_TURBINE_MANUFACTURER,
  OTHER_TURBINE_TYPE,
} from '../../technical-data/technical-resource-data/wind-specific-technical-data/wind-specific-technical-data.component'
import { GeneralDataRawValue } from '../models/main-form'
import {
  Compensation,
  PriceRequest,
  Product,
  PRODUCT,
  REGIME,
  RedispatchSettlement,
  ServiceFeeUnit,
} from '../models/price'
import moment from 'moment'
import { GENERATION_UNIT_DATE_FORMATTER } from './market-register-data.service'
import { MarketingType } from '../models/marketing-type.enum'
import { BatteryStorageStatus } from '../models/battery-storage-status.enum'

@Injectable({
  providedIn: 'root',
})
export class ProductConfigurationService {
  private readonly maxOwnConsumptionPercentage = 30
  private readonly sharePercentage = 100
  private readonly minSmallDvCapacity = 100
  private readonly maxSmallDvCapacityWithoutOwnConsumption = 5_000
  private readonly maxSmallDvCapacityWithOwnConsumption = 10_000
  private readonly maxCapacityForMonthlyDleFee = 750

  nonStandardConfig(form: GeneralDataRawValue) {
    if (form.batteryStorageStatus !== BatteryStorageStatus.NotPlanned) {
      return true
    }
    if (form.productionTechnology === ProductionTechnology.WindOnshore) {
      return this.nonStandardWind(form)
    }
    return this.nonStandardSolar(form)
  }

  private nonStandardWind(form: GeneralDataRawValue) {
    const windResources = form.technicalResources as WindTechnicalResource[]
    return windResources.some(
      (tr) => tr.turbineManufacturer === OTHER_TURBINE_MANUFACTURER || tr.turbineType === OTHER_TURBINE_TYPE
    )
  }

  private nonStandardSolar(form: GeneralDataRawValue) {
    const solarResources = form.technicalResources as SolarTechnicalResource[]
    const isSmallDv = this.classifyProduct(form) === PRODUCT.SMALL_DV
    return solarResources.some((tr) => tr.solarTrackedModules === true || (isSmallDv && tr.solarBifacial === true))
  }

  getIndicativeOfferProductAttributes(data: GeneralDataRawValue) {
    const product = this.classifyProduct(data)
    const capacity = data.totalInstalledCapacity ?? 0
    const isSmallDv = product === PRODUCT.SMALL_DV

    return {
      product,
      dleCapacityPriceEurPerMonth: isSmallDv && capacity <= this.maxCapacityForMonthlyDleFee,
      dleEnergyPriceEurPerMWh: isSmallDv && capacity > this.maxCapacityForMonthlyDleFee,
      serviceFeeUnit: (isSmallDv && capacity <= this.maxCapacityForMonthlyDleFee
        ? 'EUR/Month'
        : 'EUR/MWh') as ServiceFeeUnit,
      redispatchSettlement: this.getRedispatchSettlement(product, data.isAlreadyInOperation),
    }
  }

  extractPriceRequestData(data: GeneralDataRawValue): PriceRequest {
    const technology = data.productionTechnology!
    const productAttributes = this.getIndicativeOfferProductAttributes(data)
    const { product } = productAttributes
    const p51Hours = this.calculateParagraph51Hours(
      this.getEarliestCommissioningDate(data.technicalResources),
      data.totalInstalledCapacity!,
      technology
    )
    const monthlyMarketValueShare: 0 | 1 =
      product === PRODUCT.LARGE_DV || (data.ownConsumptionPercent ?? 0) <= this.maxOwnConsumptionPercentage ? 1 : 0
    const spotPriceShare: 0 | 1 = product === PRODUCT.LARGE_DV || monthlyMarketValueShare === 1 ? 0 : 1
    return {
      product,
      contractNumber: !data.locationNameCustomer
        ? `IOT_${moment().format('DD-MM-YYYY')}_${Math.floor(Math.random() * 1000)}`
        : data.locationNameCustomer,
      contract: {
        contractStart: data.contractStartDate!.format(GENERATION_UNIT_DATE_FORMATTER),
        contractEnd: data.contractEndDate!.format(GENERATION_UNIT_DATE_FORMATTER),
        directMarketingType: data.marketingType === MarketingType.EEGDirectMarketing ? 'promoted' : 'other',
        gooMarketing: false,
      },
      productConfiguration: {
        p51NumberOfHours: p51Hours,
        p51CompensationMethod: this.calculateP51CompensationMethod(
          product,
          p51Hours,
          data.marketingType!,
          monthlyMarketValueShare
        ),
        monthlyMarketValueShare,
        spotPriceShare,
        fixedPriceShare: 0,
        sharePercentage: product === PRODUCT.LARGE_DV ? this.sharePercentage : 0,
        dleCapacityPriceEurPerMonth: productAttributes.dleCapacityPriceEurPerMonth,
        dleEnergyPriceEurPerMWh: productAttributes.dleEnergyPriceEurPerMWh,
        serviceFeeUnit: productAttributes.serviceFeeUnit,
        redispatchSettlement: productAttributes.redispatchSettlement,
        compensationType: this.calculateOtherCompensation(product, data.marketingType!, monthlyMarketValueShare),
        //Vergütungsform
        remunerationType: monthlyMarketValueShare === 1 ? 'Marktwert' : 'Spot',
        regime: REGIME,
      },
      technicalresources: data.technicalResources.map((tr: SolarTechnicalResource | WindTechnicalResource) =>
        this.mapTechnicalResourceData(technology, tr)
      ),
      productionTechnology: mapProductionTechnology(technology),
      ownConsumptionPercent: data.ownConsumptionPercent || 0,
    }
  }

  classifyProduct(data: GeneralDataRawValue): Product {
    const isProductionTechnologySolar = data.productionTechnology === ProductionTechnology.Solar
    const hasOwnConsumption = (data.ownConsumptionPercent ?? 0) > 0
    const totalInstalledCapacity = data.totalInstalledCapacity ?? 0
    const isSmallDvCapacityRange = hasOwnConsumption
      ? totalInstalledCapacity > this.minSmallDvCapacity &&
        totalInstalledCapacity <= this.maxSmallDvCapacityWithOwnConsumption
      : totalInstalledCapacity > this.minSmallDvCapacity &&
        totalInstalledCapacity <= this.maxSmallDvCapacityWithoutOwnConsumption

    return isProductionTechnologySolar && isSmallDvCapacityRange ? PRODUCT.SMALL_DV : PRODUCT.LARGE_DV
  }

  private getRedispatchSettlement(product: Product, isAlreadyInOperation: boolean | null): RedispatchSettlement {
    if (!isAlreadyInOperation) return 'Pauschalabrechnung'
    return product === PRODUCT.SMALL_DV
      ? 'Übernahme des bisherigen RD 2.0 Abrechnungsmodells (Pauschal oder SpitzLight)'
      : 'Übernahme des bisherigen RD 2.0 Abrechnungsmodells'
  }

  private mapTechnicalResourceData(
    technology: ProductionTechnology,
    tr: SolarTechnicalResource | WindTechnicalResource
  ) {
    const common = {
      installedCapacity: tr.installedCapacityKW!,
      latitude: String(tr.latitude!),
      longitude: String(tr.longitude!),
      commissioningDate: this.getRequiredCommissioningDate(tr.commissioningDateCommon).format(
        GENERATION_UNIT_DATE_FORMATTER
      ),
      applicableValue: tr.applicableValueCentsPerkWh || undefined,
    }

    if (technology === ProductionTechnology.Solar) {
      return {
        ...common,
        bifacial: (tr as SolarTechnicalResource).solarBifacial!,
        tracking: false,
        orientation: (tr as SolarTechnicalResource).solarOrientation!,
        inclination: (tr as SolarTechnicalResource).solarTiltDegree!,
      }
    } else {
      return {
        ...common,
        manufacturer: (tr as WindTechnicalResource).turbineManufacturer!,
        turbineType: (tr as WindTechnicalResource).turbineType!,
        hubHeight: (tr as WindTechnicalResource).turbineHubHeight!,
      }
    }
  }

  private calculateOtherCompensation(
    product: Product,
    marketingType: MarketingType,
    monthlyMarketValueShare: 0 | 1
  ): Compensation {
    if (product === PRODUCT.SMALL_DV) {
      if (marketingType !== MarketingType.EEGDirectMarketing) {
        return Compensation.NOT_APPLICABLE
      }

      return monthlyMarketValueShare === 1 ? Compensation.APPLICABLE_VALUE : Compensation.NOT_APPLICABLE
    }

    if (monthlyMarketValueShare !== 1) {
      return Compensation.NOT_APPLICABLE
    }

    if (marketingType === MarketingType.EEGDirectMarketing) {
      return Compensation.APPLICABLE_VALUE
    }

    if (marketingType === MarketingType.Other) {
      return Compensation.MARKET_VALUE
    }

    return Compensation.NOT_APPLICABLE
  }

  private calculateP51CompensationMethod(
    product: Product,
    p51Hours: number | undefined,
    marketingType: MarketingType,
    monthlyMarketValueShare: 0 | 1
  ): Compensation {
    if (product === PRODUCT.LARGE_DV) {
      return p51Hours === undefined ? Compensation.NOT_APPLICABLE : Compensation.MARKET_VALUE
    }

    if (marketingType !== MarketingType.EEGDirectMarketing) {
      return Compensation.NOT_APPLICABLE
    }

    return monthlyMarketValueShare === 1 ? Compensation.MARKET_VALUE : Compensation.NOT_APPLICABLE
  }

  private calculateParagraph51Hours(
    commissioningDate: moment.Moment,
    installedCapacityKw: number,
    productionTechnology: ProductionTechnology
  ): number | undefined {
    const jan2016 = new Date('2016-01-01')
    const jul2022 = new Date('2022-07-01')
    const installedCapacityMw = installedCapacityKw / 1000

    // Case 1: Before 01.01.2016
    if (commissioningDate.isBefore(jan2016)) {
      return 0
    }

    // Case 2: Between 01.01.2016 and 30.06.2022
    if (this.isAfterOrEqual(commissioningDate, jan2016) && commissioningDate.isBefore(jul2022)) {
      if (productionTechnology === ProductionTechnology.WindOnshore && installedCapacityMw >= 3) {
        return 6
      }

      if (productionTechnology === ProductionTechnology.Solar && installedCapacityMw >= 0.5) {
        return 6
      }
    }

    // Case 3: From 01.07.2022 onwards
    if (this.isAfterOrEqual(commissioningDate, jul2022) && installedCapacityMw >= 0.5) {
      return 4
    }
    return undefined
  }

  private isAfterOrEqual(date1: moment.Moment, date2: Date): boolean {
    return date1.isAfter(date2) || date1.isSame(date2)
  }

  private toMoment(date: moment.Moment | Date | null | undefined): moment.Moment | null {
    if (!date) return null
    return date instanceof Date ? moment(date) : date.clone()
  }

  private getRequiredCommissioningDate(date: moment.Moment | Date | null | undefined): moment.Moment {
    const commissioningDate = this.toMoment(date)
    if (!commissioningDate) {
      throw new Error('commissioningDateCommon is required to build a price request')
    }
    return commissioningDate
  }

  private getEarliestCommissioningDate(
    technicalResources: (SolarTechnicalResource | WindTechnicalResource)[]
  ): moment.Moment {
    return technicalResources.reduce<moment.Moment>((earliest, tr) => {
      const commissioningDate = this.getRequiredCommissioningDate(tr.commissioningDateCommon)
      return commissioningDate.isBefore(earliest) ? commissioningDate : earliest
    }, this.getRequiredCommissioningDate(technicalResources[0].commissioningDateCommon))
  }
}
